import { useCallback, useEffect, useMemo } from "react";
import {
  IconNMRHolder,
  IconNMRHolderFilters,
  IconNMRHolderFiltersTranslator,
  iconNMRConstants,
  iconNMRHistoryConstants,
  iconNMRHoldersConstants,
  iconNMRStatusConstants,
} from "../../../api/IconNMR";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { NotAvailable, NotSet } from "../../../common/misc/UIconstants";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import styles from "../../../common/tables/GenericVirtualizedTable/commons.module.css";
import { DataSource, DataSourceFilters, dataSourceConstants } from "../../../api/DataSource";
import { IconNMRHoldersFilterForm, IconNMRHoldersFilterbar } from "./IconNMRHoldersFilterBar";
import { RenderIconNMRHolderStatus } from "../../common/IconNMRRenderer";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { useHistory, useLocation } from "react-router-dom";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import TableView from "../../../common/panels/TableView/TableView";
import { useUnpaginateOrdered } from "../../../api/BaseEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { ParameterFormatter } from "../../../ParameterViewer/TreeViewer/ParameterFormatter";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

const parameterFormatter = new ParameterFormatter();

export const switchIconNMRHoldersDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<IconNMRHolderFilters>>[] | null | undefined
): SortState<InferSortByOptions<IconNMRHolderFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "HOLDER":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-holder",
          property: sort.property,
        };

      case "STATUS":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-status",
          property: sort.property,
        };

      case "EXPNO":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-expno",
          property: sort.property,
        };
      case "DURATION":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-duration",
          property: sort.property,
        };

      case "USER":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-user",
          property: sort.property,
        };

      case "QUEUE_POSITION":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-queuePosition",
          property: sort.property,
        };

      case "DATA_SOURCE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-dataSource",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-dataSource",
          property: "DATA_SOURCE",
        };
    }
  });
};

export const useIconNMRHoldersTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"iconNMRHolders">) => {
  const {
    data: dataSources,
    status,
    fetchStatus,
    error,
  } = useUnpaginateOrdered<DataSource, DataSourceFilters>(dataSourceConstants.resource, {
    types: ["IconNMR"],
    enabled: true,
  });

  const defaults: ColumnsSettings<IconNMRHolder> = useMemo(
    () => ({
      // "default-id": { pos: 0, active: false, header: fieldLabels.id },
      "default-queuePosition": { pos: 1, active: true, header: fieldLabels.queuePosition, property: "queuePosition" },
      "default-holder": { pos: 2, active: true, header: fieldLabels.holder, property: "holder" },
      "default-status": { pos: 3, active: true, header: fieldLabels.status, property: "status" },
      "default-dataset": { pos: 4, active: true, header: fieldLabels.dataset, property: "dataset" },
      "default-expno": { pos: 5, active: true, header: fieldLabels.expno, property: "expno" },
      "default-experiment": { pos: 6, active: true, header: fieldLabels.experiment, property: "experiment" },
      "default-duration": { pos: 7, active: true, header: fieldLabels.duration, property: "duration" },
      "default-dataSource": { pos: 10, active: false, header: fieldLabels.dataSource, property: "dataSource" },
      "default-solvent": { pos: 11, active: false, header: fieldLabels.solvent, property: "solvent" },
      "default-path": { pos: 12, active: false, header: fieldLabels.path, property: "path" },
      "default-user": { pos: 13, active: false, header: fieldLabels.user, property: "user" },
      // "default-modifiedOn": { pos: 13, active: true, header: fieldLabels.modifiedOn },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults = useMemo(() => {
    let def: TableTabsDict<IconNMRHolder, IconNMRHolderFilters, IconNMRHoldersFilterForm> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: {
            sortBy: [
              {
                property: "DATA_SOURCE",
                sortDirection: "ASC",
              },
            ],
            statuses: ["Running", "Submitted"],
          },
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {
            statuses: ["Running", "Submitted"],
          },
          sidebarFilters: {},
        },
      },
    };
    if (dataSources) {
      dataSources.forEach((d, i) => {
        if (!!d.name && d.enabled)
          def[`dataSource-id-${d.id}`] = {
            tabId: `dataSource-id-${d.id}`,
            type: "fixed",
            label: d.name,
            title: d.name,
            icon: iconNMRConstants.icon,
            align: "left",
            xPos: 2 + i,
            settings: {
              columnSettings: { ...defaults, "default-instrument": { pos: 8, active: false, header: "Instrument" } },
              columnWidths: {},
              filters: {
                dataSourceIds: [d.id],
                statuses: ["Running", "Submitted"],
                sortBy: [{ property: "QUEUE_POSITION", sortDirection: "ASC" }],
              },
              sidebarFilters: {
                dataSource: [d],
                status: [
                  {
                    id: "Submitted",
                    name: "Submitted",
                  },
                  {
                    id: "Running",
                    name: "Running",
                  },
                ],
              },
            },
            forcedSettings: {
              columnSettings: {},
              columnWidths: {},
              filters: { dataSourceIds: [d.id], statuses: ["Running", "Submitted"] },
              sidebarFilters: {},
            },
          };
      });
    }
    return def as TableTabsDict<IconNMRHolder, IconNMRHolderFilters, IconNMRHoldersFilterForm>;
  }, [dataSources, defaults]);

  return { defaults, tabStoreDefaults, status, fetchStatus, error };
};

export const useIconNMRHoldersTableColumns = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"iconNMRHolders">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<IconNMRHolder> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.queuePosition,
        id: "default-queuePosition",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>
              {row.queuePosition !== null && row.queuePosition !== undefined ? row.queuePosition + 1 : NotSet}
            </span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("QUEUE_POSITION", sort, setSort),
      },
      {
        Header: fieldLabels.status,
        id: "default-status",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.status ? <RenderIconNMRHolderStatus status={row.status} /> : NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("STATUS", sort, setSort),
      },
      {
        Header: fieldLabels.solvent,
        id: "default-solvent",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.solvent ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.experiment,
        id: "default-experiment",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.experiment ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.expno,
        id: "default-expno",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.expno ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("EXPNO", sort, setSort),
      },
      {
        Header: fieldLabels.duration,
        id: "default-duration",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span> {row.duration ? parameterFormatter.formatDuration(row.duration, 1).join(" ") : NotAvailable}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("DURATION", sort, setSort),
      },
      {
        Header: fieldLabels.holder,
        id: "default-holder",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.holder ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("HOLDER", sort, setSort),
      },
      {
        Header: fieldLabels.dataset,
        id: "default-dataset",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.dataset ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.path,
        id: "default-path",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.path ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.user,
        id: "default-user",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>{row.user ?? NotSet}</span>
          </div>
        ),
        minWidth: 120,
        width: 150,
        align: "left",
        ...getAsyncSortingFn("USER", sort, setSort),
      },
      {
        Header: fieldLabels.modifiedOn,
        id: "default-modifiedOn",
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        minWidth: 120,
        width: 180,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        Header: fieldLabels.dataSource,
        id: "default-dataSource",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis}`}>
            <span>
              <LucideIcon name={dataSourceConstants.icon} /> {row.dataSource.name}
            </span>
          </div>
        ),
        minWidth: 120,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("DATA_SOURCE", sort, setSort),
      },
    ];

    return headers;
  }, [
    fieldLabels.dataSource,
    fieldLabels.dataset,
    fieldLabels.duration,
    fieldLabels.experiment,
    fieldLabels.expno,
    fieldLabels.holder,
    fieldLabels.id,
    fieldLabels.modifiedOn,
    fieldLabels.path,
    fieldLabels.queuePosition,
    fieldLabels.solvent,
    fieldLabels.status,
    fieldLabels.user,
    setSort,
    sort,
  ]);
  return { columns };
};

export const IconNMRHoldersTableWrapper = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"iconNMRHolders">) => {
  const { defaults, tabStoreDefaults, status, fetchStatus, error } = useIconNMRHoldersTableDefaults({ fieldLabels });

  if (!tabStoreDefaults)
    return (
      <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
        Fetching IconNMR instruments...
      </LoadingWrapper>
    );
  return (
    <IconNMRHoldersTable
      entityTypeId={entityTypeId}
      defaults={defaults}
      tabStoreDefaults={tabStoreDefaults}
      entityApi={entityApi}
      entityConstants={entityConstants}
      fieldLabels={fieldLabels}
      permissions={permissions}
      routes={routes}
    />
  );
};

export const IconNMRHoldersTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  defaults,
  tabStoreDefaults,
}: {
  defaults: ColumnsSettings<IconNMRHolder>;
  tabStoreDefaults: TableTabsDict<IconNMRHolder, IconNMRHolderFilters, IconNMRHoldersFilterForm>;
} & EntityTableProps<"iconNMRHolders">) => {
  const history = useHistory();

  const { resultsCount, onCountChange, onSelectionChange } = useGenericVirtualizedTable<IconNMRHolder>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<IconNMRHolder, IconNMRHolderFilters, IconNMRHoldersFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
    // initialState: initialState,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchIconNMRHoldersDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useIconNMRHoldersTableColumns({ entityConstants, fieldLabels, sort, setSort });

  // TODO: remove once generic in tab store
  const { search: queryString } = useLocation();

  useEffect(() => {
    if (!tabsLoading) {
      let params = Object.fromEntries(new URLSearchParams(queryString));
      if ("dataSourceId" in params && !!params["dataSourceId"])
        (async () => await onTabChange(`dataSource-id-${params["dataSourceId"]}`))();
    }
  }, [history, onTabChange, queryString, tabsLoading]);

  const memoizedFilters = useMemo(() => {
    return { ...filters, ...forcedFilters };
  }, [filters, forcedFilters]);

  const queryClient = useQueryClient();

  const refetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [iconNMRStatusConstants.resource] });
    queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
    queryClient.invalidateQueries({ queryKey: [iconNMRHistoryConstants.resource] });
  }, [entityConstants.resource, queryClient]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchData();
    }, 60000);
    return () => clearInterval(interval);
  }, [refetchData]);

  return (
    <TableView style={{ marginTop: 0, paddingTop: 0 }}>
      <TableView.Body>
        <TableView.Body.Sidebar defaultSize={0}>
          <IconNMRHoldersFilterbar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
            hideReset={currentTab !== "default"}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
            showAddTab={false}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<IconNMRHolder, IconNMRHolderFilters>
                filters={filters}
                excludeFilters={{
                  statuses: () => false,
                  dataSourceIds: (value) => (currentTab !== "default" ? false : !!value?.length),
                }}
                translatorConsts={IconNMRHolderFiltersTranslator}
                showAlways
              />

              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />

              <div className="flex align-center gap-5">
                <span style={{ color: "var(--gray-300)", paddingRight: "5px" }}>|</span>
                <span style={{ color: "var(--gray-500)", whiteSpace: "nowrap" }}>
                  Results with <LucideIcon name="filter" />:
                </span>
                <span className="badge">{resultsCount ?? NotAvailable}</span>
                <span style={{ color: "var(--gray-300)", paddingLeft: "5px" }}>|</span>
              </div>
              <button onClick={refetchData} className="btn btn-ghost-primary btn-xs flex align-center gap-5">
                <LucideIcon name="refresh-cw" />
                Refresh
              </button>
            </EntityTable.Controls>
            <EntityTable.Body<IconNMRHolder, IconNMRHolderFilters>
              functionRef={functionRef}
              entityConstants={iconNMRHoldersConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              //   onSelectionPermissions={onSelectionPermissions}
              //   showPermissionColumn
              loading={tabsLoading}
              disableCheckboxes
              onRowClick={() => {}}
              linkTo={() => "#"}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
